import { isAfter, add, format, subMinutes, startOfDay, endOfDay, subDays } from 'date-fns';
import isNil from 'lodash/isNil';
import { API } from '../api';
import {
    EventParticipationDto,
    IEvent,
    IEventMetrics,
    IEventsCountBySiteAndYears,
    IExportEventsRequestParams,
    IFetchEventMetricsRequestParams,
    IFetchEventsCompanyParticipationRequestParams,
    IFetchEventsRequestParams,
    IFetchResourcesRequestParams,
} from './interface';
import { IResource } from './resource';
import { IPagination } from '../IPagination';
import { ISite } from '../site/interface';
import { DATE_FORMAT_CSV, DATE_FORMAT_CSV_PERIOD } from '../commonConst';
import { exportCSVData } from '../common/exportCSVData';
import { uniq } from 'lodash';
import { parseClearPeriodValue, parseDateRangeValue } from 'src/pages/settlements/view/vendorBillItems/sidebar/utils';
import _ from 'lodash';
import { Override } from '../Override';

export const fetchAllEvents = async (params: IFetchEventsRequestParams, signal?: AbortSignal | null): Promise<IEvent[]> => {
    const pageSize = 10000;

    const events: IEvent[] = [];
    const pageList = [1];

    for await (let page of pageList) {
        const { data, meta } = await fetchEvents({ ...params, pagination: { pageSize, current: page } }, signal);

        if (page * pageSize < meta.total) {
            pageList.push(++page);
        }

        events.push(...data);
    }

    return events;
};

export const fetchEvents = async (params: IFetchEventsRequestParams, signal: AbortSignal | null = null): Promise<IPagination<IEvent>> => {
    updateMarketZoneFilter(params);

    let {
        companyId = null,
        programId = null,
        site_utility = null,
        event_completed,
        siteId = null,
        eventProgramName = null,
        event_cancelled,
        event_opt_type,
        start,
        end,
        startInRange,
        pagination = { pageSize: 10000, current: 1 },
        include,
        sorter,
        search,
        load_zone,
        market,
        account_manager,
        marketAwardDispatchId,
        sourceProgram,
        resource_telemetry_status,
    } = params;

    const limit = pagination.pageSize!;
    const offset = (pagination.current! - 1) * limit;

    if (sorter && (!sorter.field || !sorter.order)) {
        sorter = {};
    }

    const path = `/events?${new URLSearchParams({
        ...(companyId && { companyId }),
        ...(programId && { programId }),
        ...(site_utility && { site_utility }),
        ...(siteId && { siteId }),
        ...(eventProgramName && { eventProgramName }),
        ...(include && { include }),
        ...(search && { search }),
        ...(load_zone && { load_zone }),
        ...(market && { market }),
        ...(sorter?.field && { sortField: sorter.field, sortOrder: sorter.order }),
        ...(!isNil(event_cancelled) && { event_cancelled }),
        ...(!isNil(event_completed) && { event_completed }),
        ...(!isNil(event_opt_type) && { event_opt_type }),
        ...(start && { start: start.toISOString() }),
        ...(end && { end: end.toISOString() }),
        ...(startInRange && { startInRange }),
        ...(account_manager && { accountManagers: account_manager }),
        ...(marketAwardDispatchId && { marketAwardDispatchId: marketAwardDispatchId }),
        ...(sourceProgram && { sourceProgram: sourceProgram }),
        ...(resource_telemetry_status && { resource_telemetry_status }),
        offset: offset.toString(),
        limit: limit.toString(),
    })}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();
    if (response.ok) {
        return respBody;
    }
    throw new Error(response.statusText);
};

export const cancelEvent = async (eventId: number) => {
    const response = await API.fetch(`/events/${eventId}`, {
        method: 'delete',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    if (response.ok) {
        return;
    }

    const respBody: any = await response.json();
    throw new Error(respBody?.error?.message || `Cannot cancel event!`);
};

export const updateEvent = async (eventId: number, event: Partial<IEvent>) => {
    const response = await API.fetch(`/events/${eventId}`, {
        method: 'put',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(event),
    });
    const respBody: any = await response.json();

    if (response.ok) {
        return;
    }
    throw new Error(respBody?.error?.message || `Cannot update event!`);
};

export const fetchEventsBatch = async ({ batchId, include, companyId }: { batchId: string; include?: string; companyId?: number }) => {
    const query = `?${new URLSearchParams({
        ...(include && { include }),
        ...(companyId && { companyId: companyId + '' }),
    })}`;
    const response = await API.fetch(`/events/batch/${batchId}${query}`, {
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();
    if (response.ok) {
        return respBody;
    }

    throw new Error(response.statusText);
};

export const createEventBatch = async (event: any, sites: number[], companyId?: number) => {
    const query = new URLSearchParams({ ...(companyId && { companyId: companyId + '' }) });
    const response = await API.fetch(`/events/sites/batch?${query}`, {
        method: 'post',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ event, sites }),
    });

    if (response.ok) {
        return;
    }

    const respBody: any = await response.json();
    throw new Error(respBody?.error?.message || `Cannot save event!`);
};

export const createEventBatchByProgram = async (event: any, programs: number[]) => {
    const response = await API.fetch('/events/programs/batch', {
        method: 'post',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ event, programs }),
    });

    if (response.ok) {
        return;
    }

    const respBody: any = await response.json();
    throw new Error(respBody?.error?.message || `Cannot save event!`);
};

export const updateEventBatch = async (eventBatchId: string, event: any, companyId?: number) => {
    const query = `?${new URLSearchParams({ ...(companyId && { companyId: companyId + '' }) })}`;
    const response = await API.fetch(`/events/batch/${eventBatchId}${query}`, {
        method: 'put',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(event),
    });

    if (response.ok) {
        return;
    }

    const respBody: any = await response.json();
    throw new Error(respBody?.error?.message || `Cannot update batch!`);
};

export const cancelEventBatch = async (eventBatchId: string, companyId?: number) => {
    const query = `?${new URLSearchParams({ ...(companyId && { companyId: companyId + '' }) })}`;
    const response = await API.fetch(`/events/batch/${eventBatchId}${query}`, {
        method: 'delete',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    if (response.ok) {
        return;
    }

    const respBody: any = await response.json();
    throw new Error(respBody?.error?.message || `Cannot cancel batch!`);
};

export const exportEvents = async (params: IExportEventsRequestParams) => {
    let {
        start,
        end,
        startInRange,
        sorter,
        include,
        search,
        event_cancelled,
        event_completed,
        event_opt_type,
        companyId,
        programId,
        eventProgramName,
        load_zone,
        market,
        site_utility = null,
        account_manager,
        marketAwardDispatchId,
    } = params;
    updateMarketZoneFilter(params);

    sorter = sorter || { field: 'event_datetime_start', order: 'descend' };

    if (sorter && (!sorter.field || !sorter.order)) {
        sorter = {};
    }

    const path = `/events?${new URLSearchParams({
        format: 'csv',
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(companyId && { companyId: companyId.toString() }),
        ...(programId && { programId }),
        ...(eventProgramName && { eventProgramName }),
        ...(include && { include }),
        ...(search && { search }),
        ...(sorter?.field && { sortField: sorter.field, sortOrder: sorter.order }),
        ...(start && { start: start.toISOString() }),
        ...(end && { end: end.toISOString() }),
        ...(startInRange && { startInRange: startInRange.toString() }),
        ...(!isNil(event_cancelled) && { event_cancelled: event_cancelled.toString() }),
        ...(!isNil(event_completed) && { event_completed: event_completed.toString() }),
        ...(!isNil(event_opt_type) && { event_opt_type: event_opt_type.toString() }),
        ...(account_manager && { accountManagers: account_manager }),
        ...(load_zone && { load_zone }),
        ...(market && { market }),
        ...(site_utility && { site_utility }),
        ...(marketAwardDispatchId && { marketAwardDispatchId }),
    })}`;
    const response = await API.fetch(path, {
        headers: {
            'Content-Type': 'text/csv',
        },
    });

    if (response.ok) {
        const respBody: string = await response.text();
        const period =
            start && end
                ? `${format(start, DATE_FORMAT_CSV_PERIOD)}_${format(end, DATE_FORMAT_CSV_PERIOD)}`
                : `all--${format(new Date(), DATE_FORMAT_CSV)}`;
        const filename = `events-${period}.csv`;
        exportCSVData(respBody, filename);
    } else {
        const respBody: any = await response.json();
        throw new Error(respBody?.error?.message || `Cannot export data!`);
    }
};

export const NO_MARKET_TEXT = 'NO MARKET';
export const NO_LOAD_ZONE_TEXT = 'NO LOAD ZONE';

export const fetchResources = async (params: IFetchResourcesRequestParams, signal: AbortSignal | null = null): Promise<IResource[]> => {
    updateMarketZoneFilter(params);

    const { companyId = null, programId = null, search, load_zone, market, account_manager } = params;

    const path = `/sites?${new URLSearchParams({
        ...(companyId && { company_id: companyId }),
        ...(programId && { program_id: programId }),
        ...(search && { search }),
        ...(load_zone && { load_zone }),
        ...(market && { market }),
        ...(account_manager && { account_manager }),
        isClosed: false,
        offset: 0,
        limit: 10000,
        include: 'company,account,program',
    })}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'application/json',
        },
    });
    const respBody: any = await response.json();
    if (response.ok) {
        const resources = respBody.data
            .filter((site: ISite) => !site.closed_at)
            .map((site: ISite) => {
                const accountManagers = uniq(
                    site.company?.accounts?.filter(account => !!account.account_manager).map(account => account.account_manager)
                ) as string[];

                const resource: IResource = {
                    id: `${site.site_id}`,
                    company: site.company?.company_name!,
                    account_managers: accountManagers,
                    programs: site.programs.map(p => p.name),
                    site: site.site_name,
                    site_timezone: site.site_timezone,
                    zip: site.site_zip,
                    utility: site.site_utility || null,
                    event_offset: site.event_offset,
                    event_max_duration: site.event_max_duration || 0,
                    site_state: site.site_state,
                    sans: site.sans.filter(san => san.is_active).map(san => san.service_account_number),
                    site_address: site.site_address,
                    lmp_market: site.lmp_market?.toUpperCase() || NO_MARKET_TEXT,
                    site_load_zone: site.site_load_zone?.toUpperCase() || NO_LOAD_ZONE_TEXT,
                };
                return resource;
            });

        return resources;
    }

    throw new Error(respBody?.error?.message || 'Cannot fetch resources');
};

function updateMarketZoneFilter(filter: { load_zone?: string; market?: string | null; [key: string]: any }): IFetchEventsRequestParams {
    if (filter.load_zone && filter.load_zone.includes('null-')) {
        filter.load_zone = 'null';
    }

    if (filter.load_zone && filter.load_zone.includes('undefined-')) {
        filter.load_zone = undefined;
    }

    if (filter.market && filter.market.includes('null-')) {
        filter.market = 'null';
    }

    if (filter.market && filter.market.includes('undefined-')) {
        filter.market = undefined;
    }

    return filter;
}

export const fetchEventsMetrics = async (params: IFetchEventMetricsRequestParams, signal?: AbortSignal | null): Promise<IEventMetrics> => {
    updateMarketZoneFilter(params);

    const {
        companyId = null,
        site_utility = null,
        event_completed,
        eventProgramName = null,
        event_cancelled,
        event_opt_type,
        start,
        end,
        search,
        load_zone,
        market,
        account_manager,
        resource_telemetry_status,
    } = params;

    const path = `/events/metrics?${new URLSearchParams({
        ...(companyId && { companyId: companyId.toString() }),
        ...(site_utility && { site_utility }),
        ...(eventProgramName && { eventProgramName: eventProgramName.toString() }),
        ...(search && { search }),
        ...(load_zone && { load_zone }),
        ...(market && { market }),
        ...(!isNil(event_cancelled) && { event_cancelled: event_cancelled.toString() }),
        ...(!isNil(event_completed) && { event_completed: event_completed.toString() }),
        ...(!isNil(event_opt_type) && { event_opt_type: event_opt_type.toString() }),
        ...(start && { start: start.toISOString() }),
        ...(end && { end: end.toISOString() }),
        ...(account_manager && { accountManagers: account_manager.toString() }),
        ...(resource_telemetry_status && { resource_telemetry_status: resource_telemetry_status.toString() }),
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();
    if (response.ok) {
        return respBody;
    }

    throw new Error(respBody?.error?.message || `Cannot fetch event metrics!`);
};

/**
 * Event is completed if it and its post event are finished
 * @param event
 * @returns
 */
export const isCompleted = (event: IEvent) => {
    if (!event.event_id) return false;
    let postEventDate = add(new Date(event.event_datetime_start), { minutes: event.event_duration });
    postEventDate = add(postEventDate, { minutes: event.event_post_duration });
    return isAfter(new Date(), postEventDate);
};

// return date without depends on timeZone
export const toUniversalTime = (date: Date) => {
    const selectedDate = new Date(date);
    selectedDate.setSeconds(0);
    selectedDate.setMilliseconds(0);

    const timeOffset = selectedDate.getTimezoneOffset();

    return subMinutes(selectedDate, timeOffset).toISOString();
};

export async function fetchEventsCountBySites(siteIds: number[], startDate: Date, endDate: Date): Promise<{ data: IEventsCountBySiteAndYears }> {
    const path = `/events/count?${new URLSearchParams({
        siteIds: siteIds.join(','),
        start: startDate.toISOString(),
        end: endDate.toISOString(),
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })}`;

    const response = await API.fetch(path, {
        method: 'get',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    if (response.ok) {
        return await response.json();
    }

    const respBody: any = await response.json();
    throw new Error(respBody?.error?.message || `Cannot fetch site!`);
}

type EventParticipationResponseDto = Override<EventParticipationDto, { event_datetime_start_list: string[] }>;

export const fetchEventsCompanyParticipation = async (
    { filter }: IFetchEventsCompanyParticipationRequestParams,
    signal?: AbortSignal | null
): Promise<EventParticipationDto[]> => {
    const path = `/events/participation?${new URLSearchParams({
        ...prepareCompanyParticipationFilter(filter),
        include: 'nomination',
    })}`;
    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'text/csv',
        },
    });

    const respBody: any = await response.json();
    if (response.ok) {
        const data = (respBody as EventParticipationResponseDto[]).map(item => {
            return {
                ...item,
                totalNominationKw: _.sumBy(item.nomination, 'nomination_kw'),
                event_datetime_start_list: item.event_datetime_start_list.map(date => new Date(date)),
            } as EventParticipationDto;
        });

        return data;
    }

    throw new Error(respBody?.error?.message || `Cannot fetch event participation!`);
};

export const exportEventsCompanyParticipation = async (
    { filter }: IFetchEventsCompanyParticipationRequestParams,
    signal?: AbortSignal | null
): Promise<void> => {
    const path = `/events/participation?${new URLSearchParams({
        ...prepareCompanyParticipationFilter(filter),
        format: 'csv',
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        include: 'nomination',
    })}`;
    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    if (response.ok) {
        const respBody: string = await response.text();
        const filename = `events-company-participation.csv`;
        exportCSVData(respBody, filename);
    } else {
        const respBody: any = await response.json();
        throw new Error(respBody?.error?.message || `Cannot export data!`);
    }
};

function prepareCompanyParticipationFilter(filter: IFetchEventsCompanyParticipationRequestParams['filter']) {
    const { start, end } = getDateRangeFromPeriod(filter?.period);

    return {
        ...(filter.companyId && { companyId: filter.companyId.toString() }),
        ...(filter.event_programs && { event_programs: filter.event_programs }),
        ...(filter.participation_statuses && { participation_statuses: filter.participation_statuses }),
        ...(start && { start: start.toISOString() }),
        ...(end && { end: end.toISOString() }),
    };
}

function getDateRangeFromPeriod(period?: string): { start: Date | null; end: Date | null } {
    const today = new Date();

    const clearPeriod = parseClearPeriodValue(period);

    switch (clearPeriod) {
        case 'today':
            return {
                start: startOfDay(today),
                end: endOfDay(today),
            };

        case 'yesterday':
            const yesterday = subDays(today, 1);
            return {
                start: startOfDay(yesterday),
                end: endOfDay(yesterday),
            };

        case 'custom_range':
            const dateRange = parseDateRangeValue(period);

            if (!dateRange) {
                return {
                    start: null,
                    end: null,
                };
            }

            // The incoming dates carry the selected calendar day in UTC. Re-read
            // that day in the local timezone so start/end map to the local
            // start/end of day (avoids the 23:59:59Z value rolling to next day).
            const startDay = new Date(dateRange.start.getUTCFullYear(), dateRange.start.getUTCMonth(), dateRange.start.getUTCDate());
            const endDay = new Date(dateRange.end.getUTCFullYear(), dateRange.end.getUTCMonth(), dateRange.end.getUTCDate());

            return {
                start: startOfDay(startDay),
                end: endOfDay(endDay),
            };

        case 'all':
        default:
            return {
                start: null,
                end: null,
            };
    }
}
